import { useTranslation } from "@i18n";
import { useMemo, useRef, useState } from "react";
import type { ComponentRef } from "react";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { ProjectWBS } from "@Features/projects";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR, TOAST_TYPE_WARNING } from "@Services/constants";
import { linkWBSSources } from "@Features/commercial-shared/contracts/constant";
import { contractWBSServicesUrl } from "@Services/dataManager";
import { RootState } from "@Reducers/index";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

/** Per-item custom amount/percent values, keyed by WBS originalItemId. */
type CustomValues = Record<string, { amount: number; percent: number }>;

/** The grid handle `ProjectWBS` forwards; only its `dataView` is read here. */
type WBSGridHandle = NonNullable<ComponentRef<typeof ProjectWBS>>;
/**
 * One row of that grid, taken from the handle so it stays in step with what `ProjectWBS` binds.
 * `_id` (project WBS item) and `originalItemId` (the project WBS item a contract WBS row points
 * back to) are the 24-hex ObjectIds the backend feeds to `new ObjectId(...)` — see
 * `contractWBSLinkItemsRequest.data.wbsItemIds` in contracts.schema.ts.
 */
type WBSGridRow = WBSGridHandle["dataView"]["items"][number];

/** Payload passed to the parent's `setSelectedWBSItemsToAttach` callback. */
type SelectedWBSItemsToAttach =
  | { itemIds: ObjectIdString[] }
  | { itemIds: ObjectIdString[]; customValues: CustomValues }
  | { itemId: ObjectIdString; boqAvailableAmount: number };

/** Config object forwarded to the underlying `ProjectWBS` grid wrapper. */
interface LinkWBSConfigParams {
  dataSourceQuery?: object;
  dataSourceUrl?: string;
  idMapping?: string;
  showColumnForParentWbs?: boolean;
  renameHeaders?: boolean;
  allowSingleRowSelection?: boolean;
  showBoqBudgetColumn?: boolean;
  showAvailableBudgetColumn?: boolean;
}

interface LinkWBSProps {
  hideLinkWbsModal: () => void;
  wbsSource: string;
  setSelectedWBSItemsToAttach: (payload: SelectedWBSItemsToAttach) => void;
  /** A Contract id is always a 24-hex ObjectId; branded so it matches `ProjectWBS.contractId`. */
  contractId?: ObjectIdString;
  formId?: string;
  allowSingleSelection?: boolean;
  btnText?: string;
  /**
   * WBS item ids that are already linked, forwarded verbatim to `ProjectWBS.selectedWBSItems`,
   * which seeds its `disabledRows` from them. Ids, not rows — that is what the only supplier
   * (`WBSContent.tsx`) passes and what `ProjectWBS` compares against `originalItemId`.
   */
  selectedWBSItems?: ObjectIdString[];
  /** Passed by some callers but not consumed here; declared to allow the prop. */
  parentContractId?: string;
}

function LinkWBS({
  hideLinkWbsModal,
  wbsSource,
  setSelectedWBSItemsToAttach,
  contractId,
  formId,
  allowSingleSelection = false,
  btnText = "Link",
  selectedWBSItems = [],
}: LinkWBSProps) {
  const { t } = useTranslation();
  const [linkWBSBtn, setLinkWBSBtn] = useState(false);
  const dispatch = useDispatch();
  const [customValues, setCustomValues] = useState<CustomValues>({});
  const contractDetails = useSelector((state: RootState) => state.contractDetails);

  const WBSRef = useRef<ComponentRef<typeof ProjectWBS>>(null);
  const params = useParams();

  const linkWBSButtonStateEnable = (state: boolean) => {
    setLinkWBSBtn(state);
  };
  const handleLinkWBSClick = async () => {
    // `getAllSelectedItems()` is declared `TRow[]` but pushes `getItemById(id)` for every id still
    // in `selectedRowIds`, which is `undefined` for a selection carried across a refresh — so the
    // holes are real and the list is widened here rather than trusted.
    const selectedRows: Array<WBSGridRow | undefined> = WBSRef.current?.dataView.getAllSelectedItems() ?? [];

    const isAnyItemAlreadyPresent = selectedRows.some(
      (data) => data?.isAlreadyAddedInThisContract || data?.isAlreadyAddedInThisForm,
    );
    const itemIds = selectedRows.flatMap((data) => {
      const itemId = wbsSource == linkWBSSources.projectWBS ? data?._id : data?.originalItemId;
      return itemId ? [itemId] : [];
    });
    if (isAnyItemAlreadyPresent) {
      dispatch(
        showToastNotification({
          content: t("One of the selected Item is already linked in the contract"),
          type: TOAST_TYPE_ERROR,
        }),
      );
    } else if (wbsSource == linkWBSSources.projectWBS) {
      setSelectedWBSItemsToAttach({ itemIds });
    } else if (wbsSource == linkWBSSources.parentContractWBS) {
      setSelectedWBSItemsToAttach({ itemIds, customValues });
    } else if (wbsSource == linkWBSSources.contractWBS) {
      const [selectedRow] = selectedRows;
      if (selectedRow?.hasChild) {
        dispatch(
          showToastNotification({
            content: t("Parent WBS Items cannot be attached"),
            type: TOAST_TYPE_WARNING,
          }),
        );
      } else if (selectedRow?.originalItemId) {
        // Both amounts come from the contract-WBS projection, which always carries them; the
        // fallbacks only keep the subtraction from yielding NaN on a row read back through a hole.
        const boqAvailableAmount = (selectedRow.revisedAmount ?? 0) - (selectedRow.boqItemsAmount ?? 0);
        setSelectedWBSItemsToAttach({
          itemId: selectedRow.originalItemId,
          boqAvailableAmount,
        });
      }
    } else if (wbsSource == linkWBSSources.contractWBSInForm) {
      if (selectedRows.some((item) => item?.hasChild)) {
        dispatch(
          showToastNotification({
            content: t("Parent WBS Items cannot be attached"),
            type: TOAST_TYPE_WARNING,
          }),
        );
      } else {
        setSelectedWBSItemsToAttach({ itemIds });
      }
    }
  };
  const cancelLinking = () => {
    hideLinkWbsModal();
  };

  const linkWBSFooter = () => (
    <div className=" flex gap-4">
      <ButtonComponent disabled={!linkWBSBtn} buttonType={BUTTON_TYPES.GRADIENT_BUTTON} onClick={handleLinkWBSClick}>
        {btnText}
      </ButtonComponent>
      <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={cancelLinking}>
        {t("Cancel")}
      </ButtonComponent>
    </div>
  );
  const linkWBSHeader = () => {
    let subHeader = "Changes done in project WBS after linking will not effect already linked WBS";
    if ([linkWBSSources.contractWBS, linkWBSSources.contractWBSInForm].includes(wbsSource)) {
      subHeader = "All the available WBS items in this contract";
    } else if (wbsSource == linkWBSSources.parentContractWBS) {
      subHeader = "All the available WBS items in parent contract";
    }
    return (
      <div className=" w-full flex justify-between rounded-md">
        <div className="flex flex-col gap-1">
          <div>{t("WBS Items List")}</div>
          <span className="text-sm font-normal">{subHeader}</span>
        </div>
      </div>
    );
  };

  const projectWBSQuery = useMemo(
    () => ({
      contractId,
      viewType: "WBSView",
      projectId: params.projectId,
      contractVersionId: contractDetails.versionId,
      where: [
        {
          isComplex: false,
          field: "projectId",
          operator: "equal",
          ignoreCase: false,
          value: params.projectId,
        },
      ],
    }),
    [contractDetails.versionId, contractId, params.projectId],
  );

  const parentContractWBSQuery = useMemo(
    () => ({
      contractId,
      viewType: "WBSView",
      getParentItems: true,
      contractVersionId: contractDetails.versionId,
    }),
    [contractDetails.versionId, contractId],
  );

  const contractWBSQuery = useMemo(
    () => ({
      contractId,
      viewType: "WBSView",
      contractVersionId: contractDetails.versionId,
    }),
    [contractDetails.versionId, contractId],
  );
  const contractWBSInFormQuery = useMemo(
    () => ({
      contractId,
      viewType: "WBSView",
      contractVersionId: contractDetails.versionId,
      formId,
    }),
    [contractDetails.versionId, contractId, formId],
  );
  const linkWBSContent = () => {
    let configParams: LinkWBSConfigParams = {};
    if (wbsSource == linkWBSSources.projectWBS) {
      configParams = {
        dataSourceQuery: projectWBSQuery,
      };
    } else if (wbsSource == linkWBSSources.parentContractWBS) {
      configParams = {
        dataSourceQuery: parentContractWBSQuery,
        dataSourceUrl: contractWBSServicesUrl,
        idMapping: "originalItemId",
        showColumnForParentWbs: true,
        renameHeaders: true,
      };
    } else if (wbsSource == linkWBSSources.contractWBS) {
      configParams = {
        dataSourceQuery: contractWBSQuery,
        dataSourceUrl: contractWBSServicesUrl,
        idMapping: "originalItemId",
        showColumnForParentWbs: false,
        renameHeaders: true,
        allowSingleRowSelection: allowSingleSelection,
        showBoqBudgetColumn: true,
        showAvailableBudgetColumn: true,
      };
    } else if (wbsSource == linkWBSSources.contractWBSInForm) {
      configParams = {
        dataSourceQuery: contractWBSInFormQuery,
        dataSourceUrl: contractWBSServicesUrl,
        idMapping: "originalItemId",
        showColumnForParentWbs: false,
        renameHeaders: true,
        allowSingleRowSelection: allowSingleSelection,
        showBoqBudgetColumn: true,
        showAvailableBudgetColumn: false,
      };
    }
    return (
      <ProjectWBS
        selectedWBSItems={selectedWBSItems}
        formId={formId}
        // @ts-expect-error -- `LinkWBSConfigParams` does not match the modal's declared config shape; the two disagree on which fields are optional.
        configParams={configParams}
        // @ts-expect-error -- a `Dispatch<SetStateAction<CustomValues>>` is passed where the child declares a plain setter over a different value type — the same `SetStateAction` invariance wall as `FileManagerData`: the value arm needs one assignability direction and the updater arm the other, so no variance trick bridges two different `T`s.
        setCustomValues={setCustomValues}
        wbsSource={wbsSource}
        customValues={customValues}
        contractId={contractId}
        linkWBSButtonStateEnable={linkWBSButtonStateEnable}
        showOnlyGrid
        ref={WBSRef}
      />
    );
  };
  return (
    <>
      <ModalStateDialogComponent
        allowModalPadding={false}
        footerContent={linkWBSFooter()}
        width="80%"
        height={"80%"}
        headerTitle={linkWBSHeader()}
        onCancel={cancelLinking}
        isVisible
        modelContent={linkWBSContent()}
      />
    </>
  );
}
export default LinkWBS;
